import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { MaskedLines } from "@/components/MaskedLines";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CAT_IDS = ["toutes", "entretien", "compatibilite", "livraison", "garantie"];

export default function Faq() {
  const { t, lang } = useLang();
  const [faqs, setFaqs] = useState([]);
  const [cat, setCat] = useState("toutes");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqs.filter((f) => {
      const catOk = cat === "toutes" || f.category === cat;
      const text = `${f.question} ${f.answer} ${f.question_en || ""} ${f.answer_en || ""}`.toLowerCase();
      return catOk && (!q || text.includes(q));
    });
  }, [faqs, cat, query]);

  return (
    <main className="mx-auto max-w-4xl px-4 pt-32 pb-24 sm:px-8">
      <Seo title={`FAQ | NordiGuard`} description={t("faq.sub")} />

      <header className="pt-8 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("faq.eyebrow")}</p>
        <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
          <MaskedLines lines={[t("faq.title")]} />
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-slate-400">{t("faq.sub")}</p>
      </header>

      <div className="relative mt-10">
        <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="faq-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("faq.search")}
          className="w-full rounded-full border border-slate-700/80 bg-slate-900/70 py-4 pr-5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-sky-400/70 focus:ring-2 focus:ring-sky-500/20"
          style={{ paddingLeft: "3.25rem" }}
        />
      </div>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {CAT_IDS.map((id) => (
          <button
            key={id}
            data-testid={`faq-filter-${id}`}
            onClick={() => setCat(id)}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 ${
              cat === id
                ? "bg-sky-400 text-slate-950 shadow-[0_0_20px_rgba(56,189,248,0.4)]"
                : "border border-slate-700 text-slate-300 hover:border-sky-400/50 hover:text-sky-300"
            }`}
          >
            {t(`faq.cats.${id}`)}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {visible.length === 0 ? (
          <p data-testid="faq-empty-state" className="py-16 text-center text-sm text-slate-500">
            {t("faq.empty")}
          </p>
        ) : (
          <Accordion type="single" collapsible className="space-y-4">
            {visible.map((f) => (
              <AccordionItem
                key={f.id}
                value={f.id}
                data-testid={`faq-accordion-item-${f.id}`}
                className="glacial-card overflow-hidden border-sky-500/15 px-6"
              >
                <AccordionTrigger className="py-5 text-left font-display text-base font-semibold text-slate-100 hover:text-sky-300 hover:no-underline">
                  {lang === "en" ? f.question_en || f.question : f.question}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-slate-400">
                  {lang === "en" ? f.answer_en || f.answer : f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>

      <div className="glacial-card mt-14 p-8 text-center">
        <h2 className="font-display text-xl font-bold text-slate-50">{t("faq.stillTitle")}</h2>
        <p className="mt-2 text-sm text-slate-400">{t("faq.stillSub")}</p>
        <a
          href="/contact"
          data-testid="faq-contact-cta"
          className="mt-6 inline-block rounded-full bg-sky-400 px-7 py-3 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_24px_rgba(56,189,248,0.45)]"
        >
          {t("faq.cta")}
        </a>
      </div>
    </main>
  );
}
